namespace StringUtils {
  /**
   * Formats number value into USD string with dollar sign
   *
   * @param value Value to format into USD string with dollar sign
   * @returns formatted USD string with dollar sign
   */
  export function USD(value: number, signDisplay = false): string {
    const options: Intl.NumberFormatOptions | undefined = {
      style: 'currency',
      currency: 'USD',
    };
    if (signDisplay) {
      options.signDisplay = 'exceptZero';
    }
    return value ? value.toLocaleString('en-US', options) : '$0.00';
  }

  /**
   * Truncates numbers 1 million and up to short form using 'M', 'B', and 'T'
   *
   * @param value Value to format/truncate into short form
   * @returns numbers truncated to short form (ie. 1M, 2.53B, 6.3T, etc.)
   */
  export function truncate(value: number): string {
    if (value) {
      if (value >= 1e6) {
        // Only million and up will have the unit 'M', 'B', or 'T' following it
        return value.toLocaleString('en-US', {
          notation: 'compact',
          compactDisplay: 'short',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      } else {
        return String(value);
      }
    }

    return '0.00';
  }

  /**
   * Gives number appropriate sign (+ or -) and formats as a currency or percentage
   *
   * @param value value to format
   * @param style 'percent' or 'currency' format
   * @returns
   */
  export function signNumber(value: number, style: 'percent' | 'currency'): string {
    return value.toLocaleString('en-US', {
      style,
      currency: 'USD',
      signDisplay: 'exceptZero',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Checks if a password is at least 8 characters long, and has at least one number and one letter
   *
   * @param password password user enters
   * @returns true or false
   */
  export function isValidPassword(password: string): boolean {
    const regex = /^(?=.*[A-Za-z])(?=.*\d)[a-zA-Z\d\w\W]{8,}$/;
    return regex.test(password);
  }

  /**
   * Checks if a stirng contains at least one alphabetic character
   *
   * @param s string to check
   * @returns true or false
   */
  export function containsLetter(s: string) {
    return /[a-zA-Z]/.test(s);
  }

  /**
   * Checks if a string contains at least one numerical digit
   *
   * @param s string to check
   * @returns true or false
   */
  export function containsNumber(s: string) {
    return /[0-9]/.test(s);
  }

  /**
   * Format a number as a monetary value (e.g. 1.00)
   * @param n the number to format
   * @param locale the locale to use for formatting (default: en-US)
   * @returns the formatted number as a string
   */
  export function monetary(n: number, locale = 'en-US') {
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      useGrouping: true,
    }).format(n);
  }

  /**
   * Format a number as an ordinal string (e.g. 1st, 2nd, 3rd, etc.)
   *
   * @param num number to format as an ordinal string
   * @returns formatted ordinal string
   */
  export function formatOrdinal(num: number): string {
    const pr = new Intl.PluralRules('en-US', { type: 'ordinal' });
    const suffixes = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
    return `${num}${suffixes[pr.select(num)]}`;
  }

  /**
   * Replaces placeholders in a template with values from a params object.
   * Placeholders are of the form {key} where key is a string.
   * If no value is found for a key, an empty string is used.
   *
   * @param template the template string to replace placeholders in
   * @param params the object containing values to replace placeholders with
   * @returns the template string with all placeholders replaced
   */
  export function format(template: string, params: Record<string, any>) {
    return template.replace(/{(\w+)}/g, (_, key) => params[key] ?? '');
  }

  /**
   * Extracts the main domain from an email address.
   * If the email address is invalid, returns null.
   * Otherwise, returns the main domain (e.g. example.com)
   * @param email the email address to extract the main domain from
   * @returns the main domain as a string, or null if the email is invalid
   */
  export function extractMainDomain(email: string): string | null {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return null;
    }

    const domain = email.split('@')[1];

    const parts = domain.split('.');

    if (parts.length >= 2) {
      return parts.slice(-2).join('.');
    }

    return domain;
  }

  export function capitalizeWord(word: string) {
    if (!word) return '';
    word = word.trim();
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  /**
   * Capitalizes each word in a phrase or composed name.
   * Handles extra spaces and hyphenated or apostrophe names.
   * Examples:
   *   capitalizePhrase("mary ann o'brien") => "Mary Ann O'Brien"
   *   capitalizePhrase("joão-da-silva") => "João-Da-Silva"
   *   capitalizePhrase("  anna maria  ") => "Anna Maria"
   */
  export function capitalizePhrase(phrase: string): string {
    if (!phrase) return '';
    return phrase
      .trim()
      .split(/\s+/)
      .map(word =>
        word
          .split(/([-'])/) // split but keep hyphens/apostrophes as tokens
          .map(part => /^[a-zA-Z\u00C0-\u017F]+$/.test(part) ? StringUtils.capitalizeWord(part) : part)
          .join('')
      )
      .join(' ');
  }
}

export default StringUtils;
